import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Typography,
  Table,
  Button,
  Modal,
  Form as AntForm,
  Input,
  Space,
  message,
  Tag,
  Select,
  Segmented,
  Tooltip,
  Alert,
} from "antd";
import {
  PlusOutlined,
  DownloadOutlined,
  ReloadOutlined,
  CopyOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useForm, FormProvider, useWatch } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import type { TableProps } from "antd";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSearchParams } from "react-router";
import { exportCsv } from "../../utils/csv";
import { authFetch } from "../../utils/api";
import { generatePassword } from "../../utils/password-gen";
import { copyToClipboard, buildNewMemberMessage } from "../../utils/clipboard";
import {
  FormField,
  FormActions,
  DangerousActionModal,
  TargetBrandField,
} from "../../shared/form";
import {
  UserCreateSchema,
  type UserCreateDTO,
  UserResetPasswordSchema,
  type UserResetPasswordDTO,
  BatchAssignTierSchema,
  type BatchAssignTierDTO,
} from "../../schemas/user";
import { BrandScopeNotice } from "../../components/BrandScopeNotice";
import { getBrandLabel, tokenManager, type Brand } from "../../utils/tokenManager";
import { useViewScope } from "../../utils/viewScope";
import {
  canEditMemberEmail,
  memberEmailEditUserFromRow,
  MemberEmailEditModal,
  type MemberEmailEditUser,
} from "./MemberEmailEditModal";
import {
  deriveSelectedBrands,
  filterTiersByBrand,
  mergeSelectionBrandMap,
} from "./selectionBrand";

const { Title } = Typography;

interface UserRecord {
  id: number;
  brand: Brand;
  username: string;
  email: string | null;
  tierId: number | null;
  isAnonymous: boolean;
  memberAccountId: number | null;
  status: "active" | "suspended" | "banned";
  statusReason: string | null;
  tierExpiredAt: string | null;
  deviceFingerprint: string | null;
  vlessUuid: string | null;
  createdAt: string;
  updatedAt: string;
}

interface TierRecord {
  id: number;
  name: string;
  level: number;
  brand: string;
}

type AccountType = "registered" | "anonymous" | "all";

const apiFetch = async (url: string, options?: RequestInit) => {
  const res = await authFetch(url, options);
  return res.json();
};

/** Ignore malformed deep-link parameters instead of turning them into a query. */
const parseMemberAccountId = (value: string | null): number | undefined => {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
};

/**
 * 用戶管理頁面 — Pattern A 範本：
 * - 表單用 react-hook-form + Zod schema
 * - 共用 FormField / FormActions 元件
 * - 刪除改為 DangerousActionModal（type-to-confirm）
 */
export const UserList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const memberAccountId = parseMemberAccountId(
    searchParams.get("memberAccountId"),
  );
  // 檢視範圍（去品牌化 M1）— 列表跟著全域檢視走；聚合模式 brandForRequest=null。
  const { scope: viewScope, brandForRequest, isAllScope } = useViewScope();
  // 列表 / 文案顯示用的品牌 label：聚合顯示「全部品牌」，單品牌顯示該品牌。
  const viewBrandLabel = isAllScope ? "全部品牌" : getBrandLabel(viewScope);
  const normalWritesAllowed = tokenManager.hasToken();
  // 列表狀態
  const [data, setData] = useState<UserRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [keyword, setKeyword] = useState("");
  const [emailKeyword, setEmailKeyword] = useState("");
  const [accountType, setAccountType] = useState<AccountType>("registered");
  const [loading, setLoading] = useState(false);
  const [emailEditUser, setEmailEditUser] = useState<MemberEmailEditUser | null>(null);

  const accountTypeLabel = useMemo(() => {
    const labels: Record<AccountType, string> = {
      registered: "正式會員",
      anonymous: "匿名會員",
      all: "全部會員",
    };
    return labels[accountType];
  }, [accountType]);

  // Tiers 查詢表
  const [tiers, setTiers] = useState<TierRecord[]>([]);
  const tierMap = useMemo(
    () => new Map(tiers.map((t) => [t.id, t.name])),
    [tiers],
  );
  const tierOptions = useMemo(
    () => tiers.map((t) => ({ label: t.name, value: t.id })),
    [tiers],
  );

  // VPN-99：新增會員彈窗的等級下拉，依表單「目標品牌」專屬拉取（不跟檢視範圍）。
  // 檢視範圍載入的 tiers 是「看的品牌」，但新增會員選的 targetBrand 可能不同 →
  // 用檢視範圍 tier 建會員會回「方案不存在」。改成依 watchedTargetBrand 拉。
  const [addTiers, setAddTiers] = useState<TierRecord[]>([]);
  const addTierOptions = useMemo(
    () => addTiers.map((t) => ({ label: t.name, value: t.id })),
    [addTiers],
  );

  // ─── 新增用戶表單（RHF + Zod） ───
  const [addOpen, setAddOpen] = useState(false);
  const addForm = useForm<UserCreateDTO>({
    resolver: zodResolver(UserCreateSchema),
    mode: "onBlur",
    defaultValues: {
      username: "",
      password: "",
      confirmPassword: "",
      tierId: undefined as unknown as number,
      // M4：目標品牌預設空；開啟 modal 時若檢視=單品牌會顯式帶入。
      targetBrand: "",
    },
  });

  // ─── 更改密碼表單 ───
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdUserId, setPwdUserId] = useState<number | null>(null);
  const [pwdUsername, setPwdUsername] = useState<string>("");
  // M4：更改密碼操作既有 row，目標品牌 = 該 row 品牌（開 modal 時記下）。
  const [pwdBrand, setPwdBrand] = useState<Brand | null>(null);
  const pwdForm = useForm<UserResetPasswordDTO>({
    resolver: zodResolver(UserResetPasswordSchema),
    mode: "onBlur",
    defaultValues: { password: "", confirmPassword: "" },
  });

  // ─── 批量指派等級 ───
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  // M4：批量寫入的目標品牌來自被選中的既有 rows。跨頁保存的「選取鍵 → 品牌」map，
  // 選取當下（含換頁）就記下 row.brand；批量寫入前用它算 distinct brands。
  // 為什麼不只看當前頁 data：跨頁選取時 off-page 那筆 row 不在 data 裡，只靠
  // data.filter 會漏算它的品牌 → 跨品牌 selection 被誤判成同品牌 → 守衛被繞過。
  const [selectionBrandMap, setSelectionBrandMap] = useState<
    Map<number, Brand>
  >(() => new Map());
  // 全部同品牌 → 送該品牌；跨品牌（聚合檢視下可能選到不同品牌的 row，含跨頁）→
  // 單一 X-Target-Brand 無法表達，擋下。
  const selectedBrands = useMemo(
    () => deriveSelectedBrands(selectionBrandMap),
    [selectionBrandMap],
  );
  const isMixedBrandSelection = selectedBrands.length > 1;

  // 統一的選取變更處理：同步更新 keys 與跨頁品牌 map。
  const handleSelectionChange = useCallback(
    (keys: React.Key[], rows: (UserRecord | undefined)[]) => {
      setSelectedRowKeys(keys);
      setSelectionBrandMap((prev) => mergeSelectionBrandMap(prev, keys, rows));
    },
    [],
  );

  // 清空選取（批量操作完成 / 切帳號類型時）— keys 與品牌 map 一起清。
  const clearSelection = useCallback(() => {
    setSelectedRowKeys([]);
    setSelectionBrandMap(new Map());
  }, []);
  const [batchTierOpen, setBatchTierOpen] = useState(false);
  const batchTierForm = useForm<BatchAssignTierDTO>({
    resolver: zodResolver(BatchAssignTierSchema),
    mode: "onBlur",
    defaultValues: { tierId: undefined as unknown as number },
  });

  // ─── 刪除確認（type-to-confirm） ───
  const [deleteTarget, setDeleteTarget] = useState<UserRecord | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ─── 行內操作 loading ───
  const [inlineTierLoading, setInlineTierLoading] = useState<number | null>(
    null,
  );
  const [inlineStatusLoading, setInlineStatusLoading] = useState<number | null>(
    null,
  );

  /* ─── 資料載入 ─── */

  const fetchTiers = useCallback(async () => {
    try {
      const result = await apiFetch("/api/admin/tiers");
      if (result.code === 0 && Array.isArray(result.data)) {
        setTiers(result.data);
      }
    } catch {
      // ignore
    }
    // brandForRequest 進 deps：檢視範圍變更時 tiers 隨品牌換。
  }, [brandForRequest]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        sortBy: "id",
        sortOrder: "DESC",
      });
      if (keyword) params.set("keyword", keyword);
      if (emailKeyword) params.set("email", emailKeyword);
      params.set("accountType", accountType);
      if (memberAccountId !== undefined) {
        params.set("memberAccountId", String(memberAccountId));
      }

      const result = await apiFetch(`/api/admin/users?${params.toString()}`);
      if (result.code === 0 && result.data) {
        setData(Array.isArray(result.data.list) ? result.data.list : []);
        setTotal(result.data.total ?? 0);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
    // brandForRequest 進 deps：檢視範圍變更（X-Brand 隨之改）時重新拉列表。
  }, [page, pageSize, keyword, emailKeyword, accountType, memberAccountId, brandForRequest]);

  useEffect(() => {
    fetchTiers();
  }, [fetchTiers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 檢視範圍變更時回到第一頁，並清空跨頁選取（換品牌檢視後列表全換，
  // 舊選取（含其品牌 map）不該殘留，避免對到看不見的 row）。
  useEffect(() => {
    setPage(1);
    clearSelection();
  }, [brandForRequest, clearSelection]);

  // An accounting deep link is a read-only formal-account filter. Reset paging
  // and selection when it changes; row actions still use the user ID.
  useEffect(() => {
    setPage(1);
    clearSelection();
  }, [memberAccountId, clearSelection]);

  /* ─── 新增用戶 ─── */

  // 用於「複製訊息」按鈕：保留最後成功送出時的 username/password/tier
  // 即使 modal 重置欄位後也能讓 operator 轉貼
  const [lastCreated, setLastCreated] = useState<{
    username: string;
    password: string;
    tierName: string | undefined;
  } | null>(null);

  // 即時 watch 新增 form 的 username/password 用以 enable「複製訊息」按鈕
  const watchedUsername = useWatch({
    control: addForm.control,
    name: "username",
  });
  const watchedPassword = useWatch({
    control: addForm.control,
    name: "password",
  });
  const watchedTierId = useWatch({ control: addForm.control, name: "tierId" });
  // M4：目標品牌（新增會員實際落地的品牌）— 文案 / isKshield 判斷都以此為準，非 JWT。
  const watchedTargetBrand = useWatch({
    control: addForm.control,
    name: "targetBrand",
  });
  const targetBrandLabel = watchedTargetBrand
    ? getBrandLabel(watchedTargetBrand)
    : "";
  const isKshieldTarget = watchedTargetBrand === "kshield";

  // VPN-99：彈窗開啟且已選目標品牌時，依該品牌專屬拉 tiers（query.brand 優先於
  // X-Brand，不受檢視範圍影響），並清空 tierId（換品牌後殘留舊 tierId 會照樣炸
  // 「方案不存在」）。未選品牌時清空選項。
  useEffect(() => {
    if (!addOpen || !watchedTargetBrand) {
      setAddTiers([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await apiFetch(
          `/api/admin/tiers?brand=${encodeURIComponent(watchedTargetBrand)}`,
        );
        if (!cancelled && result.code === 0 && Array.isArray(result.data)) {
          setAddTiers(result.data);
        }
      } catch {
        if (!cancelled) setAddTiers([]);
      }
    })();
    // 換目標品牌 → 清舊 tierId，避免用別品牌的 tier 送出
    addForm.setValue("tierId", undefined as unknown as number);
    return () => {
      cancelled = true;
    };
  }, [addOpen, watchedTargetBrand, addForm]);

  const handleGeneratePassword = useCallback(() => {
    const pwd = generatePassword();
    addForm.setValue("password", pwd, {
      shouldDirty: true,
      shouldValidate: true,
    });
    addForm.setValue("confirmPassword", pwd, {
      shouldDirty: true,
      shouldValidate: true,
    });
    message.success("已產生新密碼");
  }, [addForm]);

  const handleCopyNewMemberMessage = useCallback(async () => {
    // 優先用最後成功送出的值（modal reset 後仍可複製），否則用當前 form 值
    const source = lastCreated ?? {
      username: watchedUsername || "",
      password: watchedPassword || "",
      tierName: watchedTierId
        ? tierMap.get(watchedTierId as number)
        : undefined,
    };
    if (!source.username || !source.password) {
      message.error("請先輸入帳號與密碼");
      return;
    }
    const text = buildNewMemberMessage({
      brandLabel: targetBrandLabel || viewBrandLabel,
      username: source.username,
      password: source.password,
      tierName: source.tierName,
    });
    const ok = await copyToClipboard(text);
    if (ok) {
      message.success("已複製訊息到剪貼簿");
    } else {
      message.error("複製失敗，請手動選取");
    }
  }, [
    targetBrandLabel,
    viewBrandLabel,
    lastCreated,
    tierMap,
    watchedPassword,
    watchedTierId,
    watchedUsername,
  ]);

  const handleAddSubmit: SubmitHandler<UserCreateDTO> = async (values) => {
    // M4：寫入顯式帶目標品牌（body.targetBrand + X-Target-Brand），絕不繼承檢視範圍。
    const result = await apiFetch("/api/admin/users", {
      method: "POST",
      headers: { "X-Target-Brand": values.targetBrand },
      body: JSON.stringify({
        username: values.username,
        password: values.password,
        tierId: values.tierId,
        targetBrand: values.targetBrand,
      }),
    });
    if (result.code === 0) {
      message.success(`${getBrandLabel(values.targetBrand)} 會員新增成功`);
      setLastCreated({
        username: result.data?.username ?? values.username,
        password: values.password,
        tierName: tierMap.get(values.tierId),
      });
      setAddOpen(false);
      addForm.reset();
      fetchData();
    } else {
      message.error(result.msg || "新增失敗");
      // 將後端錯誤訊息掛回 form（簡單版本：掛在 username 欄位）
      if (result.msg?.includes("存在")) {
        addForm.setError("username", { message: result.msg });
      }
    }
  };

  // 開啟新增 modal：重置表單，並依檢視範圍決定 targetBrand 預填。
  // 檢視=單品牌 → 顯式帶入該品牌（可再改）；檢視=all → 留空強制手選（不繼承檢視）。
  const openAddModal = useCallback(() => {
    addForm.reset({
      username: "",
      password: "",
      confirmPassword: "",
      tierId: undefined as unknown as number,
      targetBrand: brandForRequest ?? "",
    });
    setAddOpen(true);
  }, [addForm, brandForRequest]);

  /* ─── 刪除用戶（type-to-confirm） ─── */

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      // M4：操作既有 row 的寫入，目標品牌 = 該 row 的品牌（列表已知），
      // 顯式帶 X-Target-Brand，絕不繼承檢視範圍 / activeBrand。
      const result = await apiFetch(`/api/admin/users/${deleteTarget.id}`, {
        method: "DELETE",
        headers: { "X-Target-Brand": deleteTarget.brand },
      });
      if (result.code === 0) {
        message.success("用戶已刪除");
        setDeleteTarget(null);
        fetchData();
      } else {
        message.error(result.msg || "刪除失敗");
      }
    } catch {
      message.error("刪除失敗");
    } finally {
      setDeleting(false);
    }
  };

  /* ─── 更改密碼 ─── */

  const handleChangePasswordSubmit: SubmitHandler<
    UserResetPasswordDTO
  > = async (values) => {
    // M4：目標品牌 = 被改密碼那筆 row 的品牌（顯式 header，不繼承檢視範圍）。
    const headers: Record<string, string> = pwdBrand
      ? { "X-Target-Brand": pwdBrand }
      : {};
    const result = await apiFetch(
      `/api/admin/users/${pwdUserId}/reset-password`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ newPassword: values.password }),
      },
    );
    if (result.code === 0) {
      message.success("密碼已更改");
      setPwdOpen(false);
      pwdForm.reset();
    } else {
      message.error(result.msg || "更改密碼失敗");
      pwdForm.setError("password", { message: result.msg || "更改密碼失敗" });
    }
  };

  /* ─── 指派等級（行內下拉直接操作） ─── */

  const handleInlineAssignTier = async (
    userId: number,
    tierId: number,
    brand: Brand,
  ) => {
    setInlineTierLoading(userId);
    try {
      // M4：行內指派操作單一既有 row，目標品牌 = 該 row 品牌（顯式 header）。
      const result = await apiFetch("/api/admin/users/batch-assign-tier", {
        method: "POST",
        headers: { "X-Target-Brand": brand },
        body: JSON.stringify({ ids: [userId], tierId }),
      });
      if (result.code === 0) {
        message.success("等級已指派");
        fetchData();
      } else {
        message.error(result.msg || "指派失敗");
      }
    } catch {
      message.error("指派失敗");
    } finally {
      setInlineTierLoading(null);
    }
  };

  /* ─── 批量指派等級 ─── */

  const handleBatchAssignTierSubmit: SubmitHandler<BatchAssignTierDTO> = async (
    values,
  ) => {
    // M4：批量寫入的目標品牌來自選中 rows。跨品牌 selection 無法用單一 X-Target-Brand
    // 表達 → 擋下並提示改為分品牌操作（絕不繼承檢視範圍 / activeBrand）。
    if (isMixedBrandSelection) {
      message.error(
        "所選會員橫跨多個品牌，無法一次批量指派；請縮小到同一品牌後再操作。",
      );
      return;
    }
    const targetBrand = selectedBrands[0];
    if (!targetBrand) {
      message.error("無法判定所選會員的品牌，請重新選取。");
      return;
    }
    const result = await apiFetch("/api/admin/users/batch-assign-tier", {
      method: "POST",
      headers: { "X-Target-Brand": targetBrand },
      body: JSON.stringify({
        ids: selectedRowKeys as number[],
        tierId: values.tierId,
      }),
    });
    if (result.code === 0) {
      message.success(`已為 ${selectedRowKeys.length} 位用戶指派等級`);
      setBatchTierOpen(false);
      batchTierForm.reset();
      clearSelection();
      fetchData();
    } else {
      message.error(result.msg || "批量指派失敗");
    }
  };

  /* ─── 變更狀態（行內下拉直接操作） ─── */

  const handleInlineChangeStatus = async (
    userId: number,
    status: string,
    brand: Brand,
  ) => {
    setInlineStatusLoading(userId);
    try {
      // M4：變更狀態操作單一既有 row，目標品牌 = 該 row 品牌（顯式 header）。
      const result = await apiFetch(`/api/admin/users/${userId}/status`, {
        method: "PATCH",
        headers: { "X-Target-Brand": brand },
        body: JSON.stringify({ status }),
      });
      if (result.code === 0) {
        message.success("狀態已更新");
        fetchData();
      } else {
        message.error(result.msg || "更新狀態失敗");
      }
    } catch {
      message.error("更新狀態失敗");
    } finally {
      setInlineStatusLoading(null);
    }
  };

  /* ─── 重新生成 VLESS UUID ─── */

  const handleRegenerateVlessUuid = async (userId: number, brand: Brand) => {
    try {
      // M4：重新生成 UUID 操作單一既有 row，目標品牌 = 該 row 品牌（顯式 header）。
      const result = await apiFetch(
        `/api/admin/users/${userId}/regenerate-vless-uuid`,
        { method: "POST", headers: { "X-Target-Brand": brand } },
      );
      if (result.code === 0) {
        message.success("VLESS UUID 已重新生成");
        fetchData();
      } else {
        message.error(result.msg || "重新生成失敗");
      }
    } catch {
      message.error("重新生成失敗");
    }
  };

  /* ─── 複製到剪貼簿 ─── */

  const handleCopyUuid = (uuid: string) => {
    navigator.clipboard
      .writeText(uuid)
      .then(() => {
        message.success("已複製 UUID");
      })
      .catch(() => {
        message.error("複製失敗");
      });
  };

  /* ─── 搜尋 ─── */

  const handleSearch = (value: string) => {
    const searchTerm = value.trim();
    const searchedMemberAccountId = parseMemberAccountId(searchTerm);
    setPage(1);
    if (searchedMemberAccountId !== undefined) {
      // A positive numeric term is a formal account lookup. Keep it in the
      // URL so the result remains shareable and all row mutations still use
      // the returned user ID.
      setKeyword("");
      const next = new URLSearchParams(searchParams);
      next.set("memberAccountId", String(searchedMemberAccountId));
      setSearchParams(next);
      return;
    }

    setKeyword(searchTerm);
    // A text account search replaces a prior accounting deep link instead of
    // combining unrelated filters into an empty result.
    if (memberAccountId !== undefined) {
      const next = new URLSearchParams(searchParams);
      next.delete("memberAccountId");
      setSearchParams(next);
    }
  };

  const handleEmailSearch = (value: string) => {
    setPage(1);
    setEmailKeyword(value.trim());
  };

  /* ─── CSV 匯出 ─── */

  const handleExportCsv = () => {
    const columns = [
      { key: "id", title: "用戶 ID" },
      { key: "memberAccountId", title: "會員帳戶 ID" },
      { key: "brand", title: "品牌" },
      { key: "username", title: "帳號" },
      { key: "isAnonymous", title: "帳號類型" },
      { key: "tierId", title: "等級ID" },
      { key: "createdAt", title: "建立時間" },
    ];
    exportCsv(data, columns, `users-${viewScope}`);
  };

  /* ─── 表格欄位 ─── */

  const columns: NonNullable<TableProps<UserRecord>["columns"]> = [
    {
      dataIndex: "id",
      title: "用戶 ID",
      width: 80,
      sorter: true,
    },
    {
      dataIndex: "memberAccountId",
      title: "會員帳戶 ID",
      width: 130,
      render: (id: number | null) =>
        id == null ? <span style={{ color: "#999" }}>—</span> : `#${id}`,
    },
    {
      dataIndex: "username",
      title: "帳號",
    },
    {
      dataIndex: "email",
      title: "Email",
      width: 220,
      render: (email: string | null) =>
        email ? (
          <Typography.Text
            ellipsis={{ tooltip: email }}
            style={{ display: "block", maxWidth: 200 }}
          >
            {email}
          </Typography.Text>
        ) : (
          <span style={{ color: "#999" }}>—</span>
        ),
    },
    {
      dataIndex: "brand",
      title: "品牌",
      width: 90,
      render: (brand: Brand) => <Tag color="blue">{getBrandLabel(brand)}</Tag>,
    },
    {
      dataIndex: "tierId",
      title: "等級",
      width: 150,
      render: (tierId: number | null, record: UserRecord) => {
        const tierName =
          tierId != null ? (tierMap.get(tierId) ?? `Tier ${tierId}`) : "-";
        const isExpired =
          record.tierExpiredAt && dayjs(record.tierExpiredAt).isBefore(dayjs());
        return (
          <span>
            {tierName}
            {isExpired && (
              <Tag color="red" style={{ marginLeft: 4, fontSize: 11 }}>
                已到期
              </Tag>
            )}
          </span>
        );
      },
    },
    {
      dataIndex: "isAnonymous",
      title: "類型",
      width: 100,
      render: (val: boolean) =>
        val ? (
          <Tag color="orange">匿名會員</Tag>
        ) : (
          <Tag color="blue">正式會員</Tag>
        ),
    },
    {
      dataIndex: "deviceFingerprint",
      title: "設備指紋",
      width: 180,
      render: (fp: string | null) => {
        // ha:sha256(ANDROID_ID)[:40] / lt:<uuid> 雜湊值（非 raw ANDROID_ID），
        // 主要供 wudi_star 匿名用戶溯源裝置。空值（正式用戶 / K盾無匿名）顯示「—」。
        if (!fp) return <span style={{ color: "#999" }}>—</span>;
        return (
          <Typography.Text
            copyable={{ text: fp }}
            ellipsis={{ tooltip: fp }}
            style={{ maxWidth: 150, fontSize: 12, fontFamily: "monospace" }}
          >
            {fp}
          </Typography.Text>
        );
      },
    },
    {
      dataIndex: "status",
      title: "狀態",
      width: 90,
      render: (status: UserRecord["status"]) => {
        const map = {
          active: { color: "green", label: "正常" },
          suspended: { color: "orange", label: "暫停" },
          banned: { color: "red", label: "封禁" },
        };
        const info = map[status] || map.active;
        return <Tag color={info.color}>{info.label}</Tag>;
      },
    },
    {
      dataIndex: "vlessUuid",
      title: "VLESS UUID",
      width: 200,
      render: (uuid: string | null, record: UserRecord) => {
        if (!uuid) return <Tag>未生成</Tag>;
        const shortUuid = `${uuid.slice(0, 8)}...${uuid.slice(-4)}`;
        return (
          <Space size={4}>
            <Tooltip title={uuid}>
              <code style={{ fontSize: 12 }}>{shortUuid}</code>
            </Tooltip>
            <Tooltip title="複製 UUID">
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => handleCopyUuid(uuid)}
              />
            </Tooltip>
            <Tooltip title="重新生成 UUID（撤銷此用戶的 VLESS 存取）">
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: "重新生成 VLESS UUID？",
                    content: `舊 UUID 將立即失效，用戶 ${record.username} 需重新登入才能使用海外加速。`,
                    okText: "確認重新生成",
                    cancelText: "取消",
                    okButtonProps: { danger: true },
                    onOk: () =>
                      handleRegenerateVlessUuid(record.id, record.brand),
                  });
                }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
    {
      dataIndex: "createdAt",
      title: "建立時間",
      width: 170,
      sorter: true,
      render: (val: string) =>
        val ? dayjs(val).format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      title: "操作",
      width: 420,
      render: (_: unknown, record: UserRecord) => (
        <Space wrap>
          {canEditMemberEmail(record, normalWritesAllowed) && (
            <Button
              size="small"
              data-testid="users-email-edit-btn"
              aria-label="修改 Email"
              onClick={() => setEmailEditUser(memberEmailEditUserFromRow(record))}
            >
              修改 Email
            </Button>
          )}
          <Button
            size="small"
            data-testid="users-password-btn"
            aria-label="更改密碼"
            onClick={() => {
              setPwdUserId(record.id);
              setPwdUsername(record.username);
              setPwdBrand(record.brand);
              pwdForm.reset({ password: "", confirmPassword: "" });
              setPwdOpen(true);
            }}
          >
            更改密碼
          </Button>
          <Select
            size="small"
            data-testid="users-tier-btn"
            aria-label="指派等級"
            value={record.tierId}
            loading={inlineTierLoading === record.id}
            disabled={inlineTierLoading === record.id}
            style={{ width: 130 }}
            placeholder="指派等級"
            // VPN-99：依該 row 品牌過濾 tier（聚合檢視下 tiers 為全品牌，
            // 不過濾會選到別品牌 tier → 指派回「方案不存在」）。
            options={filterTiersByBrand(tiers, record.brand).map((t) => ({
              label: t.name,
              value: t.id,
            }))}
            onChange={(tierId) =>
              handleInlineAssignTier(record.id, tierId, record.brand)
            }
          />
          <Select
            size="small"
            data-testid="users-status-btn"
            aria-label="變更狀態"
            value={record.status}
            loading={inlineStatusLoading === record.id}
            disabled={inlineStatusLoading === record.id}
            style={{ width: 90 }}
            options={[
              { label: "正常", value: "active" },
              { label: "暫停", value: "suspended" },
              { label: "封禁", value: "banned" },
            ]}
            onChange={(status) =>
              handleInlineChangeStatus(record.id, status, record.brand)
            }
          />
          <Button
            size="small"
            danger
            data-testid="users-delete-btn"
            aria-label="刪除用戶"
            onClick={() => setDeleteTarget(record)}
          >
            刪除
          </Button>
        </Space>
      ),
    },
  ];

  /* ─── 渲染 ─── */

  return (
    <div className="page-enter">
      {/* 標題列 */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "flex-start",
          alignItems: "center",
          marginBottom: 24,
          gap: 12,
        }}
      >
        <Title level={4} style={{ margin: 0 }} data-testid="users-title">
          會員管理
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openAddModal}
          data-testid="users-add-btn"
        >
          新增會員
        </Button>
      </div>

      <BrandScopeNotice kind="members" style={{ marginBottom: 12 }} />

      {memberAccountId !== undefined && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`目前僅顯示會員帳戶 ID #${memberAccountId} 對應的服務帳戶`}
        />
      )}

      <Segmented<AccountType>
        data-testid="users-account-type-tabs"
        value={accountType}
        style={{ marginBottom: 12 }}
        options={[
          { label: "正式會員", value: "registered" },
          { label: "匿名會員", value: "anonymous" },
          { label: "全部會員", value: "all" },
        ]}
        onChange={(value) => {
          setPage(1);
          clearSelection();
          setAccountType(value);
        }}
      />

      {/* 搜尋 + 篩選列 */}
      <div
        className="mobile-filters"
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "flex-start",
          alignItems: "center",
          marginBottom: 12,
          gap: 8,
        }}
      >
        <Input.Search
          placeholder={`搜尋帳號或會員帳戶 ID（${viewBrandLabel} ${accountTypeLabel}）`}
          allowClear
          onSearch={handleSearch}
          style={{ width: 240, maxWidth: "100%" }}
          data-testid="users-search"
        />
        <Input.Search
          placeholder="搜尋 Email"
          allowClear
          onSearch={handleEmailSearch}
          style={{ width: 240, maxWidth: "100%" }}
          data-testid="users-email-search"
        />
        <Space wrap>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportCsv}
            data-testid="users-export-btn"
            aria-label="匯出 CSV"
          >
            匯出 CSV
          </Button>
          <Tooltip
            title={
              isMixedBrandSelection
                ? "所選會員橫跨多個品牌，請縮小到同一品牌後再批量指派"
                : undefined
            }
          >
            <Button
              disabled={selectedRowKeys.length === 0 || isMixedBrandSelection}
              onClick={() => {
                batchTierForm.reset();
                setBatchTierOpen(true);
              }}
              data-testid="users-batch-tier-btn"
              aria-label="批量指派等級"
            >
              批量指派等級
            </Button>
          </Tooltip>
        </Space>
      </div>

      {/* 用戶列表 */}
      <Table
        dataSource={data}
        loading={loading}
        rowKey="id"
        columns={columns}
        scroll={{ x: 800 }}
        data-testid="users-table"
        rowSelection={{
          selectedRowKeys,
          // 跨頁保留選取；沒有這個，翻頁會清掉其他頁的選取，
          // 也讓「跨頁跨品牌」情境根本無法構成 → 守衛的跨頁防護才有意義。
          preserveSelectedRowKeys: true,
          onChange: (keys, rows) => handleSelectionChange(keys, rows),
        }}
        locale={{
          emptyText: loading ? undefined : (
            <div style={{ padding: "40px 0" }}>
              <p>
                尚無 {viewBrandLabel} {accountTypeLabel}
              </p>
              {accountType === "registered" && (
                <Button type="primary" onClick={openAddModal}>
                  建立第一位會員
                </Button>
              )}
            </div>
          ),
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '500', '1000'],
          showTotal: (t) => `共 ${t} 筆`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />

      {/* 新增用戶彈窗 */}
      <Modal
        title="新增會員"
        open={addOpen}
        onCancel={() => {
          setAddOpen(false);
          addForm.reset();
        }}
        footer={null}
        destroyOnHidden
      >
        <Alert
          type={isKshieldTarget ? "warning" : "info"}
          showIcon
          style={{ marginBottom: 16 }}
          message={
            !watchedTargetBrand
              ? "請先選擇目標品牌；此帳號只會建立在所選品牌，不會出現在其他品牌。"
              : isKshieldTarget
                ? "K盾只能由後台建立正式帳號，不提供註冊或匿名試用。"
                : `此帳號會建立在 ${targetBrandLabel}，不會出現在其他品牌。`
          }
        />
        <FormProvider {...addForm}>
          <AntForm
            layout="vertical"
            onFinish={addForm.handleSubmit(handleAddSubmit)}
          >
            <TargetBrandField<UserCreateDTO> name="targetBrand" />
            <FormField<UserCreateDTO>
              name="username"
              label="帳號"
              required
              help="4-20 字元，僅限英數，首位不可為 0"
              control={<Input placeholder="例如 alice01" autoComplete="off" />}
            />
            <FormField<UserCreateDTO>
              name="password"
              label={
                <Space
                  style={{ justifyContent: "space-between", width: "100%" }}
                >
                  <span>密碼</span>
                  <Button
                    type="link"
                    size="small"
                    icon={<KeyOutlined />}
                    onClick={handleGeneratePassword}
                    data-testid="users-generate-password-btn"
                    style={{ padding: 0 }}
                  >
                    產生 8 位密碼
                  </Button>
                </Space>
              }
              required
              help="至少 8 字元，需含大小寫英文與數字（可一鍵產生 8 位英數混雜密碼）"
              control={
                <Input.Password
                  placeholder="輸入初始密碼"
                  autoComplete="new-password"
                  data-testid="users-add-password-input"
                />
              }
            />
            <FormField<UserCreateDTO>
              name="confirmPassword"
              label="確認密碼"
              required
              control={
                <Input.Password
                  placeholder="再次輸入以確認"
                  autoComplete="new-password"
                />
              }
            />
            <FormField<UserCreateDTO>
              name="tierId"
              label="等級"
              required
              control={
                <Select
                  placeholder={
                    watchedTargetBrand ? "請選擇等級" : "請先選擇目標品牌"
                  }
                  // VPN-99：依目標品牌專屬拉取的等級選項（非檢視範圍），
                  // 避免用錯品牌 tier 建會員回「方案不存在」。
                  options={addTierOptions}
                  disabled={!watchedTargetBrand}
                  allowClear
                />
              }
            />
            <Space
              style={{
                width: "100%",
                justifyContent: "flex-end",
                marginBottom: 12,
              }}
            >
              <Tooltip
                title={
                  watchedUsername && watchedPassword
                    ? "複製帳號 / 密碼 / 方案訊息給操作員或客戶"
                    : "請先輸入帳號與密碼"
                }
              >
                <Button
                  icon={<CopyOutlined />}
                  onClick={handleCopyNewMemberMessage}
                  disabled={!watchedUsername || !watchedPassword}
                  data-testid="users-copy-message-btn"
                >
                  複製訊息
                </Button>
              </Tooltip>
            </Space>
            <FormActions
              onCancel={() => {
                setAddOpen(false);
                addForm.reset();
              }}
              submitting={addForm.formState.isSubmitting}
              dirty={addForm.formState.isDirty}
              submitLabel="建立用戶"
            />
          </AntForm>
        </FormProvider>
      </Modal>

      <MemberEmailEditModal
        user={emailEditUser}
        open={emailEditUser !== null}
        onClose={() => setEmailEditUser(null)}
        onSuccess={fetchData}
      />

      {/* 更改密碼彈窗 */}
      <Modal
        title={pwdUsername ? `更改密碼：${pwdUsername}` : "更改密碼"}
        open={pwdOpen}
        onCancel={() => {
          setPwdOpen(false);
          pwdForm.reset();
        }}
        footer={null}
        destroyOnHidden
      >
        <FormProvider {...pwdForm}>
          <AntForm
            layout="vertical"
            onFinish={pwdForm.handleSubmit(handleChangePasswordSubmit)}
          >
            <FormField<UserResetPasswordDTO>
              name="password"
              label="新密碼"
              required
              help="至少 8 字元，需含大小寫英文與數字"
              control={
                <Input.Password
                  placeholder="輸入新密碼"
                  autoComplete="new-password"
                />
              }
            />
            <FormField<UserResetPasswordDTO>
              name="confirmPassword"
              label="確認密碼"
              required
              control={
                <Input.Password
                  placeholder="再次輸入以確認"
                  autoComplete="new-password"
                />
              }
            />
            <FormActions
              onCancel={() => {
                setPwdOpen(false);
                pwdForm.reset();
              }}
              submitting={pwdForm.formState.isSubmitting}
              dirty={pwdForm.formState.isDirty}
              submitLabel="更改密碼"
            />
          </AntForm>
        </FormProvider>
      </Modal>

      {/* 批量指派等級彈窗 */}
      <Modal
        title={`批量指派等級（已選 ${selectedRowKeys.length} 位用戶）`}
        open={batchTierOpen}
        onCancel={() => {
          setBatchTierOpen(false);
          batchTierForm.reset();
        }}
        footer={null}
        destroyOnHidden
      >
        <FormProvider {...batchTierForm}>
          <AntForm
            layout="vertical"
            onFinish={batchTierForm.handleSubmit(handleBatchAssignTierSubmit)}
          >
            <FormField<BatchAssignTierDTO>
              name="tierId"
              label="等級"
              required
              control={
                <Select
                  placeholder="請選擇等級"
                  // VPN-99：依選取集合的唯一品牌過濾 tier（跨品牌 selection 已由
                  // isMixedBrandSelection 擋提交；此處單品牌才給選項，避免選錯品牌 tier）。
                  options={filterTiersByBrand(
                    tiers,
                    isMixedBrandSelection ? null : selectedBrands[0],
                  ).map((t) => ({ label: t.name, value: t.id }))}
                  allowClear
                />
              }
            />
            <FormActions
              onCancel={() => {
                setBatchTierOpen(false);
                batchTierForm.reset();
              }}
              submitting={batchTierForm.formState.isSubmitting}
              dirty={batchTierForm.formState.isDirty}
              submitLabel="指派"
            />
          </AntForm>
        </FormProvider>
      </Modal>

      {/* 刪除用戶 — 危險動作 type-to-confirm */}
      <DangerousActionModal
        open={!!deleteTarget}
        title={`刪除用戶：${deleteTarget?.username ?? ""}`}
        warning={
          <>
            此動作將軟刪除用戶、撤銷所有 refresh token 並清除連線記錄。
            <br />
            此動作無法從 UI 復原。
          </>
        }
        confirmKeyword={deleteTarget?.username ?? ""}
        confirmHint={
          <>
            為防止誤刪，請輸入此用戶的帳號 <code>{deleteTarget?.username}</code>{" "}
            確認：
          </>
        }
        confirming={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        confirmLabel="確認刪除"
      />
    </div>
  );
};
